import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.waitForGSAP().then(() => {
      gsap.registerPlugin(ScrollTrigger)
      this.initAll()
    })
  }

  disconnect() {
    if (typeof ScrollTrigger !== "undefined") {
      ScrollTrigger.getAll().forEach(t => t.kill())
    }
    this.destroyCursor()
  }

  waitForGSAP() {
    return new Promise(resolve => {
      if (typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined") {
        return resolve()
      }
      const check = setInterval(() => {
        if (typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined") {
          clearInterval(check)
          resolve()
        }
      }, 50)
    })
  }

  initAll() {
    this.initNavbar()
    this.initScrollAnimations()
    this.initCursor()
    this.initAccentLines()
    ScrollTrigger.refresh()
  }

  initNavbar() {
    const navbar = document.querySelector('[data-animate="navbar"]')
    if (!navbar) return

    gsap.from(navbar, {
      y: -20,
      opacity: 0,
      duration: 0.8,
      ease: "power2.out",
      delay: 0.1
    })
  }

  initScrollAnimations() {
    // Títulos de sección
    gsap.utils.toArray('[data-animate="title"]').forEach(el => {
      gsap.from(el, {
        scrollTrigger: {
          trigger: el,
          start: "top bottom",
          once: true
        },
        opacity: 0,
        y: 30,
        duration: 0.9,
        ease: "power3.out"
      })
    })

    // Labels de sección
    gsap.utils.toArray('[data-animate="label"]').forEach(el => {
      gsap.from(el, {
        scrollTrigger: {
          trigger: el,
          start: "top bottom",
          once: true
        },
        opacity: 0,
        x: -20,
        duration: 0.6,
        ease: "power2.out"
      })
    })

    // Cards con stagger
    gsap.utils.toArray('[data-animate="cards"]').forEach(container => {
      const cards = container.querySelectorAll('[data-animate="card"]')
      if (!cards.length) return

      gsap.from(cards, {
        scrollTrigger: {
          trigger: container,
          start: "top 92%",
          toggleActions: "play none none none"
        },
        opacity: 0,
        y: 40,
        duration: 0.7,
        stagger: 0.1,
        ease: "power3.out"
      })
    })

    // Fade-in genérico
    gsap.utils.toArray('[data-animate="fade"]').forEach(el => {
      gsap.from(el, {
        scrollTrigger: {
          trigger: el,
          start: "top bottom",
          once: true
        },
        opacity: 0,
        duration: 0.8,
        ease: "power2.out"
      })
    })

    // Split horizontal
    gsap.utils.toArray('[data-animate="from-left"]').forEach(el => {
      gsap.from(el, {
        scrollTrigger: {
          trigger: el,
          start: "top 92%",
          toggleActions: "play none none none"
        },
        opacity: 0,
        x: -50,
        duration: 0.9,
        ease: "power3.out"
      })
    })

    gsap.utils.toArray('[data-animate="from-right"]').forEach(el => {
      gsap.from(el, {
        scrollTrigger: {
          trigger: el,
          start: "top 92%",
          toggleActions: "play none none none"
        },
        opacity: 0,
        x: 50,
        duration: 0.9,
        ease: "power3.out"
      })
    })

    // Imagen con scale-up
    gsap.utils.toArray('[data-animate="image"]').forEach(el => {
      gsap.from(el, {
        scrollTrigger: {
          trigger: el,
          start: "top bottom",
          once: true
        },
        opacity: 0,
        scale: 1.04,
        duration: 1.1,
        ease: "power2.out"
      })
    })

    // Count-up para números
    gsap.utils.toArray('[data-animate="counter"]').forEach(el => {
      const raw = el.textContent.trim()
      const match = raw.match(/^([+\-]?)([0-9.,]+)(.*)$/)
      if (!match) return

      const prefix = match[1]
      const numStr = match[2].replace(/\./g, "").replace(/,/g, ".")
      const numEnd = parseFloat(numStr)
      const suffix = match[3]
      const isInteger = Number.isInteger(numEnd)

      const obj = { val: 0 }
      gsap.to(obj, {
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          toggleActions: "play none none none"
        },
        val: numEnd,
        duration: 2,
        ease: "power2.out",
        onUpdate() {
          const display = isInteger
            ? Math.round(obj.val).toLocaleString("es-PY")
            : obj.val.toFixed(1)
          el.textContent = `${prefix}${display}${suffix}`
        }
      })
    })
  }

  initAccentLines() {
    gsap.utils.toArray('[data-animate="accent-line"]').forEach(el => {
      gsap.from(el, {
        scrollTrigger: {
          trigger: el,
          start: "top 90%",
          toggleActions: "play none none none"
        },
        scaleX: 0,
        transformOrigin: "left center",
        duration: 0.7,
        ease: "power3.out"
      })
    })
  }

  initCursor() {
    if (!window.matchMedia("(pointer: fine)").matches) return

    const cursor = document.createElement("div")
    cursor.id = "tayi-cursor"
    document.body.appendChild(cursor)
    document.body.classList.add("custom-cursor-active")
    this._cursor = cursor

    const xTo = gsap.quickTo(cursor, "x", { duration: 0.35, ease: "power3.out" })
    const yTo = gsap.quickTo(cursor, "y", { duration: 0.35, ease: "power3.out" })

    this._onMouseMove = (e) => {
      xTo(e.clientX)
      yTo(e.clientY)
    }

    this._onMouseEnterInteractive = () => cursor.classList.add("cursor-hover")
    this._onMouseLeaveInteractive = () => cursor.classList.remove("cursor-hover")
    this._onMouseLeaveWindow = () => cursor.classList.add("cursor-hidden")
    this._onMouseEnterWindow = () => cursor.classList.remove("cursor-hidden")

    window.addEventListener("mousemove", this._onMouseMove)
    document.addEventListener("mouseleave", this._onMouseLeaveWindow)
    document.addEventListener("mouseenter", this._onMouseEnterWindow)

    const interactives = document.querySelectorAll("a, button, [role='button'], input, textarea, select")
    interactives.forEach(el => {
      el.addEventListener("mouseenter", this._onMouseEnterInteractive)
      el.addEventListener("mouseleave", this._onMouseLeaveInteractive)
    })
    this._interactives = interactives
  }

  destroyCursor() {
    if (this._cursor) {
      this._cursor.remove()
      document.body.classList.remove("custom-cursor-active")
    }
    if (this._onMouseMove) window.removeEventListener("mousemove", this._onMouseMove)
    if (this._onMouseLeaveWindow) document.removeEventListener("mouseleave", this._onMouseLeaveWindow)
    if (this._onMouseEnterWindow) document.removeEventListener("mouseenter", this._onMouseEnterWindow)
    if (this._interactives) {
      this._interactives.forEach(el => {
        el.removeEventListener("mouseenter", this._onMouseEnterInteractive)
        el.removeEventListener("mouseleave", this._onMouseLeaveInteractive)
      })
    }
  }

}
